import React, { CSSProperties } from 'react';

// material-ui
import Radio from '@mui/material/Radio';

import styled from 'styled-components';

// theme
import aampeTheme from '../../constants/aampeTheme';

// components
import AampeTooltip from '../shared/AampeTooltip/AampeTooltip';

interface RadioInputStyledProps {
    isDisabled?: boolean;
    isChecked?: boolean;
}

interface RadioInputProps extends RadioInputStyledProps {
    descriptionText?: string;
    value: string;
    selectedOption: string;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    name?: string;
    helperText?: string;
    disableStateTooltip?: string;
    className?: string;
    radioButtonStyle?: CSSProperties;
}

const RadioInput: React.FC<React.PropsWithChildren<RadioInputProps>> = ({
    descriptionText,
    helperText,
    value,
    selectedOption,
    onChange,
    name,
    isDisabled,
    disableStateTooltip = '',
    className,
    radioButtonStyle,
}) => {
    const checked = React.useMemo(
        () => selectedOption === value,
        [selectedOption, value],
    );
    const radioId = React.useId();

    return (
        <AampeTooltip title={isDisabled ? disableStateTooltip : ''}>
            <RadioButton className={className} htmlFor={radioId}>
                <StyledRadio
                    id={radioId}
                    name={name}
                    checked={checked}
                    value={value}
                    onChange={onChange}
                    disableFocusRipple
                    disableRipple
                    disableTouchRipple
                    isDisabled={isDisabled}
                    isChecked={checked}
                    disabled={isDisabled}
                    style={radioButtonStyle}
                />
                <RadioButtonContent className="radio-content">
                    <DescriptionText isDisabled={isDisabled}>
                        {descriptionText}
                    </DescriptionText>
                    <HelperText isDisabled={isDisabled}>
                        {helperText}
                    </HelperText>
                </RadioButtonContent>
            </RadioButton>
        </AampeTooltip>
    );
};

const RadioButton = styled.label(() => ({
    display: 'flex',
    cursor: 'pointer',
    alignItems: 'flex-start',
}));

const RadioButtonContent = styled.div(() => ({
    padding: `${aampeTheme.spacing(1.5)} ${aampeTheme.spacing(0)}`,
}));

const DescriptionText = styled('p').withConfig({
    shouldForwardProp: (prop) => prop !== 'isDisabled',
})<RadioInputStyledProps>(({ isDisabled }) => ({
    color: isDisabled
        ? aampeTheme.colors.neutral09 + '80'
        : aampeTheme.colors.neutral09,
    fontSize: '16px',
    fontWeight: 500,
}));

// HelperText component
const HelperText = styled('p').withConfig({
    shouldForwardProp: (prop) => prop !== 'isDisabled',
})<RadioInputStyledProps>(({ isDisabled }) => ({
    color: isDisabled
        ? aampeTheme.colors.solidWG02
        : aampeTheme.colors.neutral09,
    fontSize: '14px',
}));

// StyledRadio component
const StyledRadio = styled(Radio).withConfig({
    shouldForwardProp: (prop) => !['isDisabled', 'isChecked'].includes(prop),
})<RadioInputStyledProps>(({ isDisabled, isChecked }) => ({
    '&:hover': {
        backgroundColor: `${aampeTheme.colors.interactiveWG01} !important`,
    },
    '& svg': {
        color: isDisabled
            ? aampeTheme.colors.bordersWG01
            : isChecked
            ? aampeTheme.colors.solidA01
            : aampeTheme.colors.solidWG02,
    },
}));

export default RadioInput;
